import * as React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import MenuButton from './MenuButton';
import MenuContent from './MenuContent';
// import CardAlert from './CardAlert';
import { useAuth } from '../../context/AuthContext';

function SideMenuMobile({ open, toggleDrawer }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile, logout } = useAuth();

  // Detect demo mode
  const isDemo = location.pathname.startsWith('/demo');

  // Demo user data
  const demoUser = {
    displayName: 'Demo User',
  };

  // Get display info
  const displayName = isDemo
    ? demoUser.displayName
    : (user?.displayName || userProfile?.displayName || 'User');

  // Base path for navigation
  const basePath = isDemo ? '/demo' : '/app';

  const handleProfile = () => {
    toggleDrawer(false)();
    navigate(`${basePath}/profile`);
  };

  const handleSettings = () => {
    toggleDrawer(false)();
    navigate(`${basePath}/settings`);
  };

  const handleCalendar = () => {
    toggleDrawer(false)();
    navigate(`${basePath}/grow-log`);
  };

  const handleLogout = async () => {
    toggleDrawer(false)();
    if (isDemo) {
      navigate('/');
    } else {
      await logout();
      navigate('/');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={toggleDrawer(false)}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        [`& .${drawerClasses.paper}`]: {
          backgroundImage: 'none',
          backgroundColor: 'background.paper',
        },
      }}
    >
      <Stack
        sx={{
          maxWidth: '70dvw',
          height: '100%',
        }}
      >
        <Stack direction="row" sx={{ p: 2, pb: 0, gap: 1 }}>
          <Stack
            direction="row"
            sx={{ gap: 1, alignItems: 'center', flexGrow: 1, p: 1 }}
          >
            <Avatar
              sizes="small"
              alt={displayName}
              src="/static/images/avatar/7.jpg"
              sx={{ width: 24, height: 24 }}
            />
            <Typography component="p" variant="h6">
              {displayName}
            </Typography>
          </Stack>
          <MenuButton onClick={handleCalendar}>
            <CalendarMonthRoundedIcon />
          </MenuButton>
          <MenuButton showBadge>
            <NotificationsRoundedIcon />
          </MenuButton>
        </Stack>
        <Divider />
        <Stack sx={{ flexGrow: 1 }}>
          <MenuContent />
          <Divider />
          {/* Profile & Settings Section */}
          <List dense sx={{ px: 1, py: 0.5 }}>
            <ListItem disablePadding>
              <ListItemButton onClick={handleProfile} sx={{ borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PersonRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Profile"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleSettings} sx={{ borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <SettingsRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
        </Stack>
        {/* <CardAlert /> */}
        <Stack sx={{ p: 2 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<LogoutRoundedIcon />}
            onClick={handleLogout}
          >
            {isDemo ? 'Exit Demo' : 'Logout'}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}

SideMenuMobile.propTypes = {
  open: PropTypes.bool,
  toggleDrawer: PropTypes.func.isRequired,
};

export default SideMenuMobile;
